import { useEffect, useState } from "react";
import type { QuizDetail } from "../../../shared/types";
import { OPTION_KEYS, mediaUrl } from "../../../shared/format";
import { Ico } from "../../components/shell";
import { AtlShell, ATL_ANS } from "../../components/atelier";
import { api, ApiError, hostQuiz } from "../../api";
import { alertDialog } from "../../components/Dialog";
import LaunchModal from "../host/LaunchModal";
import { navigate } from "../../router";

export default function QuizView({ quizId }: { quizId: string }) {
  const [quiz, setQuiz] = useState<QuizDetail | null>(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [launchOpen, setLaunchOpen] = useState(false);

  useEffect(() => {
    api.getQuiz(quizId).then((r) => setQuiz(r.quiz)).catch(() => setErr(true));
  }, [quizId]);

  if (err) {
    return (
      <div className="atl" style={{ display: "grid", placeItems: "center", minHeight: "100vh", padding: 40 }}>
        <div className="atl-card" style={{ maxWidth: 440, padding: 32, textAlign: "center" }}>
          <div className="atl-eyebrow atl-eyebrow--accent">Something collapsed in the oven</div>
          <h2 className="atl-serif" style={{ fontSize: 24, marginTop: 8 }}>We couldn't load this quiz</h2>
          <p style={{ fontSize: 14, color: "var(--atl-soft)", fontWeight: 500, marginTop: 8 }}>
            Even master bakers get a flat loaf now and then. Catch your breath and try again.
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 22 }}>
            <button className="atl-btn atl-btn--primary" onClick={() => location.reload()}>
              <Ico name="clock" size={15} /> Try again
            </button>
            <button className="atl-btn atl-btn--ghost" onClick={() => navigate("/quizzes")}>
              <Ico name="home" size={15} /> Back to quizzes
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="atl" style={{ display: "grid", placeItems: "center", minHeight: "100vh", padding: 40 }}>
        <div style={{ textAlign: "center" }}>
          <div className="atl-eyebrow atl-eyebrow--accent">Preheating</div>
          <h2 className="atl-serif" style={{ fontSize: 24, marginTop: 8 }}>Warming the ovens…</h2>
          <div style={{ width: 220, height: 6, borderRadius: 6, background: "var(--atl-faint)", margin: "20px auto 0", overflow: "hidden", opacity: 0.4 }}>
            <div
              style={{
                height: "100%",
                width: "55%",
                borderRadius: 6,
                background: "linear-gradient(90deg, var(--atl-gold), var(--atl-accent), var(--atl-gold))",
                backgroundSize: "200% 100%",
                animation: "bk-shimmer 1.4s linear infinite",
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  function host() {
    setLaunchOpen(true);
  }

  async function duplicate() {
    try {
      const r = await api.duplicateQuiz(quiz!.id);
      navigate(`/builder/${r.quiz.id}`);
    } catch (e) {
      alertDialog(e instanceof ApiError ? e.message : "Could not duplicate the quiz");
    }
  }

  const count = quiz.questions.length;

  return (
    <AtlShell
      active="Quizzes"
      title={quiz.title}
      sub={`${count} QUESTION${count === 1 ? "" : "S"} · READ-ONLY`}
      actions={
        <>
          <button className="atl-btn atl-btn--ghost" onClick={() => navigate("/quizzes")}>← Back</button>
          <button className="atl-btn atl-btn--ghost" onClick={duplicate}>Duplicate</button>
          <button className="atl-btn atl-btn--primary" disabled={count === 0 || busy} onClick={host}>
            <Ico name="play" size={15} /> Host
          </button>
        </>
      }
    >
      <div style={{ padding: "22px 30px", display: "flex", flexDirection: "column", gap: 14, maxWidth: 820, overflow: "hidden" }}>
        <span className="atl-chip atl-chip--gold" style={{ alignSelf: "flex-start" }}>
          Shared with you · read-only — host or duplicate it; only the owner can edit.
        </span>

        {count === 0 ? (
          <div className="atl-card" style={{ padding: 32, textAlign: "center" }}>
            <div className="atl-eyebrow atl-eyebrow--accent">Empty</div>
            <h2 className="atl-serif" style={{ fontSize: 22, marginTop: 8 }}>No questions in this quiz yet</h2>
            <p style={{ fontSize: 14, color: "var(--atl-soft)", fontWeight: 500, marginTop: 8 }}>
              Duplicate it to start adding your own questions.
            </p>
            <button className="atl-btn atl-btn--ghost" style={{ marginTop: 18 }} onClick={duplicate}>Duplicate</button>
          </div>
        ) : (
          quiz.questions.map((qq, i) => (
            <div key={qq.id} className="atl-card" style={{ padding: 22 }}>
              <div style={{ display: "flex", gap: 11, alignItems: "baseline", marginBottom: 12 }}>
                <span className="atl-mono" style={{ fontWeight: 700, color: "var(--atl-accent)" }}>Q{i + 1}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="atl-serif" style={{ fontSize: 19, lineHeight: 1.25 }}>{qq.prompt}</div>
                  <div className="atl-eyebrow" style={{ marginTop: 6 }}>
                    {qq.category ? `${qq.category} · ` : ""}{qq.timeLimit}s · {qq.points} PTS
                  </div>
                </div>
              </div>

              {qq.imageKey && (
                <img src={mediaUrl(qq.imageKey)} alt="" style={{ maxHeight: 160, borderRadius: 12, marginBottom: 12, display: "block" }} />
              )}

              <div style={{ display: "grid", gridTemplateColumns: qq.options.length <= 2 ? "1fr" : "1fr 1fr", gap: 10 }}>
                {qq.options.map((o, oi) => (
                  <div
                    key={o.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "12px 14px",
                      borderRadius: 11,
                      background: o.correct ? "#f1f5ea" : "var(--atl-surface)",
                      border: `1px solid ${o.correct ? "var(--atl-c)" : "var(--atl-line)"}`,
                    }}
                  >
                    <span
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: "50%",
                        background: ATL_ANS[oi] ?? "var(--atl-a)",
                        display: "grid",
                        placeItems: "center",
                        flex: "0 0 auto",
                        fontFamily: "var(--atl-serif)",
                        fontWeight: 600,
                        fontSize: 13,
                        color: "#fff",
                      }}
                    >
                      {(o.key ?? OPTION_KEYS[oi] ?? "").toUpperCase()}
                    </span>
                    <span style={{ flex: 1, fontWeight: 500, fontSize: 14 }}>{o.text || OPTION_KEYS[oi]?.toUpperCase()}</span>
                    {o.correct && <span style={{ color: "var(--atl-c)" }}><Ico name="check" size={16} /></span>}
                  </div>
                ))}
              </div>

              {qq.chefNote && (
                <div style={{ marginTop: 12, fontSize: 13.5, color: "var(--atl-soft)", fontWeight: 500 }}>
                  <span className="atl-serif" style={{ fontStyle: "italic", color: "var(--atl-ink)" }}>Note —</span> {qq.chefNote}
                </div>
              )}
            </div>
          ))
        )}
      </div>
      {launchOpen && quiz && (
        <LaunchModal quizId={quiz.id} quizTitle={quiz.title} onClose={() => setLaunchOpen(false)} />
      )}
    </AtlShell>
  );
}
